import React, { useMemo, useState } from "react";
import { ChevronDownIcon, XCircleFillIcon } from '@primer/octicons-react';
import { Box, Button, SelectPanel } from "@primer/react";
import { VariantType } from '@primer/react/lib/Button/types';
import { useCommonContext } from "../../contexts/common";
import { sf_hinhthucmiengiam } from "../../models/response/category/sf_hinhthucmiengiam";

type ComboBoxnhomMienGiamProps = {
    dataSource: sf_hinhthucmiengiam[],
    isReadonly?: boolean,
    value?: number | number[],
    onValueChanged: (id: number | number[] | null) => void,
    className?: string,
    isShowClearButton?: boolean,
    preText?: string,
    stylingMode?: "outlined" | "filled" | "underlined",
    width?: number | string,
    height?: number,
    placeholder?: string,
    variant?: VariantType,
    maxWidth?: any,
    maxItemsDisplayText?: number,
}

const ComboBoxNhomMienGiam = (props: ComboBoxnhomMienGiamProps) => {
    const { translate } = useCommonContext();
    const [open, setOpen] = useState(false);
    const [filter, setFilter] = useState("");
    
    const store = useMemo(() => {
        const uniqueMienGiam = Array.from(
            new Set(props.dataSource.map(miengiam => miengiam.nhom_mien_giam))
        ).filter(id => id !== null && id !== undefined && id !== 0);
        
        return uniqueMienGiam
            .sort((a, b) => a - b)
            .map(items => ({
                id: items,
                text: 'Nhóm ' + items,
                search: `Nhóm ${items}`,
            }));
    }, [props.dataSource]);

    const filteredData = useMemo(() => {
        return store.filter(item =>
            item.search.toLowerCase().includes(filter.toLowerCase())
        );
    }, [store, filter]);

    const selectedValues = useMemo(() => {
        if (Array.isArray(props.value)) {
            return props.value;
        }
        return props.value !== undefined && props.value !== null ? [props.value] : [];
    }, [props.value]);

    const selectedData = useMemo(() => {
        return store.filter(item => selectedValues.includes(item.id));
    }, [store, selectedValues]);

    const displayText = useMemo(() => {
        if (selectedData.length === 0) {
            return props.placeholder || translate("Chọn nhóm MG");
        }
        if (selectedData.length === 1) {
            return selectedData[0].text;
        }
        const maxItems = props.maxItemsDisplayText || 3;
        if (selectedData.length <= maxItems) {
            return selectedData.map(item => item.text).join(', ');
        }
        return `${selectedData.length} items selected`;
    }, [selectedData, props.placeholder, props.maxItemsDisplayText, translate]);

    const handleSelectedChange = (data: any) => {
        if (!props.isReadonly) {
            if (Array.isArray(data)) {
                const ids = data.map(item => item.id);
                props.onValueChanged(ids);
            } else if (data) {
                props.onValueChanged([data.id]);
            } else {
                props.onValueChanged([]);
            }
        }
    };

    const handleClear = () => {
        if (props.isShowClearButton && !props.isReadonly) {
            props.onValueChanged(null);
        }
    };

    return (
        <Box sx={{ width: props.width || 150 }}>
            <SelectPanel
                renderAnchor={({ children, 'aria-labelledby': ariaLabelledBy, ...anchorProps }) => (
                    <Button
                        variant={props.variant}
                        disabled={props.isReadonly}
                        sx={{
                            width: '100%',
                            height: props.height || 33,
                        }}
                        trailingAction={ChevronDownIcon}
                        aria-labelledby={` ${ariaLabelledBy}`}
                        {...anchorProps}
                    >
                        <p style={{ 
                            maxWidth: props.maxWidth, 
                            overflow: "hidden", 
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontSize: '12px',
                        }}>
                            {displayText}
                        </p>
                    </Button>
                )}
                title={
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Box sx={{ flex: 1 }}>
                            {translate("Chọn nhóm MG")}
                        </Box>
                        {props.isShowClearButton && selectedData.length > 0 && (
                            <Button
                                trailingVisual={XCircleFillIcon}
                                variant='invisible'
                                sx={{
                                    color: "danger.emphasis"
                                }}
                                onClick={handleClear}
                            >
                                Bỏ chọn
                            </Button>
                        )}
                    </Box>
                }
                placeholderText="Tìm kiếm..."
                open={open}
                onOpenChange={setOpen}
                items={filteredData}
                selected={selectedData}
                onSelectedChange={handleSelectedChange}
                onFilterChange={setFilter}
                showItemDividers={true}
                overlayProps={{ width: 'large', height: 'medium' }}
            />
        </Box>
    );
}

export default ComboBoxNhomMienGiam;